/**
 * Sign-in hook
 *
 * Provides sign-in functionality with OTP verification and rate limiting
 */

import { useAuthActions } from "@convex-dev/auth/react";
import { useAction } from "convex/react";
import { useCallback, useState } from "react";
import { api } from "convex/_generated/api";
import i18n from "i18next";
import type {
  AuthError,
  AuthStep,
  SignInCredentials,
  SignInResult,
} from "../types";
import { normalizeEmail, validateEmail, sanitizeAuthError } from "../utils";

export interface UseSignInOptions {
  /** Called on successful sign-in (after OTP verification) */
  onSuccess?: () => void;
  /** Called on sign-in error */
  onError?: (error: AuthError) => void;
}

export interface UseSignInReturn {
  /** Current step in the sign-in flow */
  step: AuthStep;
  /** Sign in with email and password */
  signIn: (credentials: SignInCredentials) => Promise<SignInResult>;
  /** Sign in with form data (for form onSubmit handlers) */
  signInWithForm: (formData: FormData) => Promise<SignInResult>;
  /** Verify OTP code */
  verifyOtp: (code: string) => Promise<SignInResult>;
  /** Resend the OTP code */
  resendOtp: () => Promise<void>;
  /** Whether sign-in is in progress */
  isLoading: boolean;
  /** Current error message if any */
  error: string | null;
  /** Clear the error */
  clearError: () => void;
  /** Email used for OTP verification (shown in UI) */
  verificationEmail: string | null;
  /** Go back to credentials step */
  backToCredentials: () => void;
}

/**
 * Hook for sign-in functionality
 *
 * Handles:
 * - Email validation
 * - Rate limiting (automatic)
 * - Sign-in attempt
 * - Error handling
 * - Rate limit reset on success
 *
 * @param options - Optional callbacks for success/error
 * @returns Sign-in functions and state
 *
 * @example
 * ```tsx
 * function SignInForm() {
 *   const { signIn, isLoading, error } = useSignIn({
 *     onSuccess: () => router.push("/dashboard"),
 *   });
 *
 *   const handleSubmit = async (e: FormEvent) => {
 *     e.preventDefault();
 *     await signIn({ email, password });
 *   };
 *
 *   return (
 *     <form onSubmit={handleSubmit}>
 *       {error && <p className="error">{error}</p>}
 *       <button disabled={isLoading}>Sign In</button>
 *     </form>
 *   );
 * }
 * ```
 */
export function useSignIn(options: UseSignInOptions = {}): UseSignInReturn {
  const { signIn: convexSignIn } = useAuthActions();
  const checkRateLimit = useAction(api.auth.checkSignInRateLimit);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<AuthStep>("credentials");
  const [verificationEmail, setVerificationEmail] = useState<string | null>(
    null,
  );
  const [storedCredentials, setStoredCredentials] =
    useState<SignInCredentials | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const backToCredentials = useCallback(() => {
    setStep("credentials");
    setError(null);
  }, []);

  const signIn = useCallback(
    async (credentials: SignInCredentials): Promise<SignInResult> => {
      setIsLoading(true);
      setError(null);

      const { email, password } = credentials;
      const normalizedEmail = normalizeEmail(email);

      // Validate email format
      if (!validateEmail(normalizedEmail)) {
        const authError: AuthError = {
          code: "VALIDATION_INVALID_INPUT",
          message: i18n.t("auth:errors.invalidEmail"),
        };
        setError(authError.message);
        setIsLoading(false);
        options.onError?.(authError);
        return { success: false, error: authError };
      }

      try {
        await checkRateLimit({ email: normalizedEmail });

        // Create form data for Convex auth
        const formData = new FormData();
        formData.set("email", normalizedEmail);
        formData.set("password", password);
        formData.set("flow", "signIn");

        // With OTP verification enabled, signIn resolves without error
        // but user is NOT yet authenticated — they need to verify their email
        await convexSignIn("password", formData);

        // Store credentials for potential resend, then show OTP step
        setStoredCredentials({ email: normalizedEmail, password });
        setVerificationEmail(normalizedEmail);
        setStep("otp-verification");
        setError(null);

        return { success: true };
      } catch (err) {
        const rawMessage = err instanceof Error ? err.message : "";
        const errorMessage = sanitizeAuthError(rawMessage, "signIn");
        const authError: AuthError = {
          code: "AUTH_INVALID_CREDENTIALS",
          message: errorMessage,
        };

        setError(errorMessage);
        options.onError?.(authError);
        return { success: false, error: authError };
      } finally {
        setIsLoading(false);
      }
    },
    [checkRateLimit, convexSignIn, options],
  );

  const verifyOtp = useCallback(
    async (code: string): Promise<SignInResult> => {
      if (!verificationEmail) {
        return {
          success: false,
          error: { code: "NO_EMAIL", message: "No email to verify" },
        };
      }

      setIsLoading(true);
      setError(null);

      try {
        const formData = new FormData();
        formData.set("email", verificationEmail);
        formData.set("code", code);
        formData.set("flow", "email-verification");

        await convexSignIn("password", formData);

        options.onSuccess?.();
        return { success: true };
      } catch (err) {
        const rawMessage = err instanceof Error ? err.message : "";
        const errorMessage = sanitizeAuthError(rawMessage, "otp");
        setError(errorMessage);
        return {
          success: false,
          error: { code: "INVALID_OTP", message: errorMessage },
        };
      } finally {
        setIsLoading(false);
      }
    },
    [convexSignIn, verificationEmail, options],
  );

  const resendOtp = useCallback(async () => {
    if (!storedCredentials) return;

    setIsLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.set("email", storedCredentials.email);
      formData.set("password", storedCredentials.password);
      formData.set("flow", "signIn");

      await convexSignIn("password", formData);
    } catch {
      // Resend may error but OTP will still be sent
    } finally {
      setIsLoading(false);
    }
  }, [convexSignIn, storedCredentials]);

  const signInWithForm = useCallback(
    async (formData: FormData): Promise<SignInResult> => {
      const email = formData.get("email") as string;
      const password = formData.get("password") as string;
      return signIn({ email, password });
    },
    [signIn],
  );

  return {
    step,
    signIn,
    signInWithForm,
    verifyOtp,
    resendOtp,
    isLoading,
    error,
    clearError,
    verificationEmail,
    backToCredentials,
  };
}
